import type { Booking } from '../api/bookings';

/** Only the two numbers — so a row shaped for a list, not a whole booking, can be labelled too. */
type Numbered = Pick<Booking, 'documentNumber' | 'quotationNumber'>;

/**
 * Which number a booking answers to, and what kind of number it is.
 *
 * <p>
 * <strong>Three stages, three answers, and the gaps are the point.</strong> An enquiry is a
 * conversation and carries no number at all — a shop fielding twenty a day would otherwise burn
 * twenty entries out of a register whose gaps its accountant has to explain. A quotation gets its
 * own <code>QT</code> series, because a priced offer is a document a customer takes away and refers
 * to. The <code>BK</code> series starts only at confirmation.
 * </p>
 *
 * <p>
 * That last move — booking number at confirmation rather than at tentative hold, where the first
 * implementation put it — is the same argument applied one stage further along. A hold that lapses
 * un-confirmed is exactly as much of a non-event as an enquiry that goes nowhere, and numbering it
 * leaves a hole in the booking register for something that never became a booking.
 * </p>
 *
 * <p>
 * A confirmed booking keeps its quotation number in the data for history, but answers to the
 * booking number: the precedence here is what stops two documents printing different references
 * for one thing.
 * </p>
 */

export type ReferenceKind = 'booking' | 'quotation' | 'unnumbered';

export interface BookingReference {
  kind: ReferenceKind;
  /** Empty only when {@link kind} is `unnumbered`. */
  number: string;
  /** What the document calls itself — the heading on a printed copy. */
  documentTitle: string;
}

/**
 * The reference to show for this booking.
 *
 * Never throws and never returns undefined: an enquiry is a legitimate state with no number, and
 * every caller has to render *something*, so the unnumbered case is a value rather than an absence
 * each screen handles its own way.
 */
export function bookingReference(booking: Numbered): BookingReference {
  const documentNumber = booking.documentNumber.trim();

  if (documentNumber) {
    return {
      kind: 'booking',
      number: documentNumber,
      documentTitle: 'Booking confirmation',
    };
  }

  const quotationNumber = booking.quotationNumber?.trim() ?? '';

  if (quotationNumber) {
    return { kind: 'quotation', number: quotationNumber, documentTitle: 'Quotation' };
  }

  return { kind: 'unnumbered', number: '', documentTitle: 'Enquiry' };
}

/**
 * What to put where a screen expects an identifier.
 *
 * An enquiry gets a word rather than a blank, because an empty cell in a register reads as data
 * that failed to load rather than as a booking that has not been numbered yet.
 */
export function referenceLabel(booking: Numbered): string {
  const reference = bookingReference(booking);

  return reference.kind === 'unnumbered' ? 'Enquiry — not numbered' : reference.number;
}

/**
 * Whether this booking has taken a number out of a register.
 *
 * Used to decide whether a barcode can be printed at all: a document whose barcode encodes an empty
 * string is a symbol that scans as nothing, which is worse than a document with no barcode.
 */
export function isNumbered(booking: Numbered): boolean {
  return bookingReference(booking).kind !== 'unnumbered';
}
